import { Button, ModalBody, ModalDialog, ModalFooter, ModalHeader, ModalOverlay } from '@flos/react-ui';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfigContext } from '../context/ConfigContext';
import { getPageIdFirst } from '../helpers/getPageIdFirst';
import useService from '../hooks/useService';
import { SavedForm, getSavedFormDataByFormIdServiceConfig, saveFormDataDelete } from '../services/saveFormData';
import useAppState from '../state/appState';
import { trackContinueWithSavedFormData, trackDeleteSavedFormData } from '../utils/tracking';
import { SubmitContext } from '../context/SubmitContext';

interface LoadSavedDataProps {
    loadSource: string;
    skipLoadPrompt: boolean;
    isDeeplink: boolean;
    isFromHighwayPath: boolean | undefined;
}
const LoadSavedData = ({ skipLoadPrompt, isDeeplink, isFromHighwayPath }: LoadSavedDataProps) => {
    const setAllFormValues = useAppState((state) => state.setAllFormValues);
    const formConfig = useAppState((state) => state.formConfig);
    const hasScalepointWidget = JSON.stringify(formConfig).includes(`"type":"scalepointwidget"`);
    const resetFormValues = useAppState((state) => state.resetFormValues);
    const setIsResumedFromSave = useAppState((state) => state.setResumedFromSave);
    const resumeFromSave = useAppState((state) => state.resumeFromSave);
    const formId = useAppState((state) => state.formId);
    const { isSubmitting } = useContext(SubmitContext);

    const topConfig = useContext(ConfigContext);
    const navigate = useNavigate();

    const [modalOpen, setModalOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const [hasAddedItemsInScalepointWidget, setHasAddedItemsInScalepointWidget] = useState(false);

    const { data: savedFormData, resetData: resetSavedFormData } = useService<SavedForm>(
        getSavedFormDataByFormIdServiceConfig(formConfig, topConfig)
    );

    const closeModal = useCallback(() => {
        if (!formConfig) {
            return;
        }

        setModalOpen(false);

        if (!isDeeplink) {
            const pageIdFirst = getPageIdFirst(formConfig);
            navigate(pageIdFirst);
        }
    }, [formConfig, isDeeplink, navigate]);

    const continueWithSavedData = useCallback(() => {
        if (savedFormData) {
            if (formConfig) {
                if (skipLoadPrompt && !isDeeplink) {
                    // If skipLoadPrompt=true the user select a saved form at highway
                    trackContinueWithSavedFormData(formConfig, 'skip-load-prompt');
                } else if (isFromHighwayPath) {
                    // If continue from save by overlay from select a form in highway (not the draft list)
                    trackContinueWithSavedFormData(formConfig, 'from-highway-path');
                } else if (isDeeplink && skipLoadPrompt) {
                    // Called from deeplink with skipLoadPrompt=true (eg QR code)
                    trackContinueWithSavedFormData(formConfig, 'from-deeplink');
                } else {
                    // If continue from save by overlay and select continue with saved data (not from highway path and draft list)
                    trackContinueWithSavedFormData(formConfig, 'other-source');
                }
            }

            setIsResumedFromSave({
                isResumed: true,
                firstSavedDate: savedFormData.firstSavedDate
            });
            setAllFormValues(savedFormData.formValues);
            closeModal();
        }
    }, [
        savedFormData,
        formConfig,
        setAllFormValues,
        setIsResumedFromSave,
        closeModal,
        isFromHighwayPath,
        isDeeplink,
        skipLoadPrompt
    ]);

    const startFreshForm = async () => {
        if (!formConfig) {
            return;
        }

        try {
            setIsDeleting(true);
            resetSavedFormData();
            resetFormValues();
            await saveFormDataDelete(formConfig, topConfig);
            trackDeleteSavedFormData(formConfig);
            closeModal();
        } catch (error) {
            console.error('Could not delete form: ' + formConfig.formId, error);
        }
    };

    useEffect(() => {
        if (!isSubmitting) {
            if (savedFormData && !resumeFromSave.isResumed) {
                console.log('# savedFormData.formValues=', savedFormData.formValues);
                if (skipLoadPrompt) {
                    continueWithSavedData();
                } else {
                    // all scalepoint widgets elementId is 'scalepointToken'
                    if (savedFormData.formValues?.scalepointToken?.scalepointItemsInBasket?.length > 0) {
                        setHasAddedItemsInScalepointWidget(true);
                        console.log('# User hasAddedItemsInScalepointWidget! ');
                    }
                    setModalOpen(true);
                }
            }
        }
    }, [
        isSubmitting,
        savedFormData,
        continueWithSavedData,
        skipLoadPrompt,
        resumeFromSave.isResumed,
        isFromHighwayPath,
        formConfig
    ]);

    return (
        <ModalOverlay isOpen={modalOpen} onDismiss={() => {}} id="saveModal">
            <ModalDialog>
                <ModalHeader></ModalHeader>
                <ModalBody>
                    <h4>
                        Du er allerede gået i gang med at udfylde en{' '}
                        {formId === 'medicinsk-forhaandsvurdering' ? 'ansøgning' : 'anmeldelse'}. Vil du fortsætte den?
                    </h4>

                    {/* 
                        We only want the user to see this extra message if any Scalepoint items was added when filling out the form the first time.
                        (the Scalepoint widget might be part of the form config, but might never actually have been rendered to the customer)
                    */}
                    {hasScalepointWidget && hasAddedItemsInScalepointWidget && (
                        <p>
                            De genstande, du måske allerede har registreret, kan være slettet. Du skal derfor tilføje
                            genstandene igen, da vi kun kan gemme dem i kort tid.
                        </p>
                    )}
                </ModalBody>
                <ModalFooter className="load-saved-btn-container">
                    <Button onClick={continueWithSavedData} theme="primary">
                        Fortsæt
                    </Button>
                    <Button
                        onClick={startFreshForm}
                        theme="secondary"
                        isLoading={isDeleting}
                        loadingText="Slet og start forfra"
                    >
                        Slet og start forfra
                    </Button>
                </ModalFooter>
            </ModalDialog>
        </ModalOverlay>
    );
};

export default LoadSavedData;
